import {
  AnmeldelseType,
  BedrageriMetodeTypeSvar,
  DatakrimSvarType,
  DatakrimType,
  EgneTiltakType,
  ElektronikkType,
  HandlingType,
  HarBankenStansetOverforingType,
  HendelsesstedValg,
  KjennemerkeKategori,
  KjennemerketypeType,
  OverforingFormaalType,
  OverforingType,
  SamtykkeVirksomhetKanSeAnmeldelsen,
  TilbakeforingType,
  TilegnetPersoninformasjonVerdiType,
  TilegnetVerdiFysiskIdType,
  TilegnetVerdiType,
} from "@politiet/anmeldelse-lib/types/anmeldelseUtkast.typer";
import skjematekst_nb from "../nb/skjematekst";
import {
  AnmeldelseType as DigaAnmeldelseType,
  BrukstyveriType,
  GjerningsstedType,
  SkadeverkType,
} from "@politiet/anmeldelse-lib/types/digaAnmeldelseBegrenset.typer";
import { SVVFargekode } from "../../skjema/anmeldelse/gjerning/Handlinger/gjenstander/components/felles/kjoretoy/SVVFargekoder.typer";

const skjematekst: typeof skjematekst_nb = {
  felles: {
    anmeldelseType: {
      TYVERI_SKADEVERK_INNBRUDD: "Tjuveri, innbrot og skadeverk",
      BEDRAGERI: "Bedrageri",
    } satisfies Record<AnmeldelseType, string>,
    stegvelger: {
      ferdig: {
        ariaLabel: "Steg fullført.",
      },
      navbar: {
        ariaLabel: "Navbar",
      },
    },
    virksomhet: {
      orgnummer: "Organisasjonsnummer",
      valgtVirksomhet: {
        slett: "Slett",
        beliggenhetsadresse: "Beliggenhetsadresse",
        aktivitet: "Aktivitet",
      },
      slettVirksomhet: "Slett verksemd",
    },
    knapper: {
      rediger: {
        label: "Endre",
        ariaLabel: "Endre",
        ariaLabelIndexed: "Endre nummer {{nr}}",
      },
      lukk: {
        ariaLabel: "Lukk",
      },
    },
    feilmelding: {
      ikon: {
        ariaLabel: "Feilmelding",
      },
      sum: {
        kunTallITallfelt:
          "Dette feltet kan berre innehalde tal. Andre teikn som komma, punktum og bokstavar er ikkje tillat. Rund av til nærmaste heile tal om du forsøker å skrive eit desimaltal",
      },
    },
    components: {
      pagination: {
        forrige: {
          ariaLabel: "Førre side",
        },
        numerertSide: {
          ariaLabel: "Gå til side {{side}}",
        },
        neste: {
          ariaLabel: "Neste side",
        },
      },
      sokefelt: {
        ariaLabel: "Søk",
      },
      modal: {
        ariaLabel: "Lukk vindauge",
      },
      erstatningskrav: {
        ariaLabel: "Erstatningskrav dokumentasjon",
      },
      handelsvarer: {
        slett: {
          ariaLabel: "Slett gjenstand",
        },
      },
      autocomplete: {
        ingenTreff: "Ingen treff",
        laster: "Lastar...",
      },
      inputDate: {
        toggleDatoVelgerKnapp: {
          closed: "Opne datoveljar",
          open: "Lukk datoveljar",
        },
        placeholder: "dd.mm.åååå",
        errorMessages: {
          parseError: "Klarte ikkje tyda dato, bruk formatet dd.mm.åååå",
        },
        lukkKnapp: "Fullfør",
        labels: {
          labelDayButton_today: "I dag, {{label}}",
          labelDayButton_selected: "{{label}} valt",
          labelWeekNumber: "Veke {{weekNumber}}",
          labelNext: "Neste månad",
          labelPrevious: "Førre månad",
          labelMonthDropdown: "Vel månad",
          labelYearDropdown: "Vel år",
        },
      },
      inputDateTime: {
        timePicker: {
          timeLabel: "Time",
          timeAriaLabel: "Velj time",
          minuttLabel: "Minutt",
          minuttAriaLabel: "Velj minutt",
        },
        placeholder: "dd.mm.åååå tt:mm",
        errorMessages: {
          parseError: "Klarte ikkje tyda dato, bruk formatet dd.mm.åååå tt:mm",
        },
      },
    },
  },
  feilside: {
    notFound: {
      tittel: "Beklagar, denne sida finn me ikkje",
      info: "Det kan hende du må logga inn for å sjå sida, eller så kan det hende ho ikkje finst. Har du sjekka at adressa er skriven rett?",
      infoInnlogget: "Har du sjekka at adressa er skriven rett?",
    },
    forbidden: {
      tittel: "Du har ikkje tilgang til å sjå denne sida",
      info: "Har du sjekka at adressa er skriven rett?",
    },
    default: {
      tittel: "Noko feil har skjedd",
      info: "Last inn sida på nytt eller prøv igjen seinare",
    },
    knapper: {
      tjenester: "Sjå tenestene til politiet",
      forsiden: "Gå til forsiden for meldingar",
      logginn: "Logg inn",
      lastInn: "Last inn sida på nytt",
    },
  },
  anmeldelse: {
    autoslettMelding_one: "Utkastet blir sletta om {{count}} dag",
    autoslettMelding_other: "Utkastet blir sletta om {{count}} dagar",
    av_anmeldelse_nummer: "av melding nummer",
    chips: {
      INNBRUDD: "Innbrot",
      SKADEVERK: "Skadeverk",
      TYVERI: "Tjuveri",
      BEDRAGERI: "Bedrageri",
      DATAKRIM: "Datakriminalitet",
    },
    fornaermede_mangler: "Fornærma manglar",
    lovbrudd_mangler: "-- Lovbrudd manglar --",
    fortsett_utfylling: "Fortsett utfyllinga",
    fra: "Frå",
    gaa_til_anmeldelse: "Gå til melding",
    gjerningTyper: {
      INNBRUDD: "Innbrot",
      SKADEVERK: "Skadeverk",
      TYVERI: "Tjuveri",
    },
    ingen_anmeldelser_innsendt: "Ingen innsende meldingar",
    ingen_anmeldelser_paabegynt: "Ingen påbyrja meldingar",
    kort: {
      diga: {
        chip: "Eldre melding",
        type: {
          TYVERI_AV_SYKKEL: "Tjuveri av sykkel",
          TYVERI_AV_MOBILTELEFON: "Tjuveri av mobiltelefon",
          TYVERI_FLERE_GJENSTANDER: "Tjuveri av andre gjenstandar",
          KJENNEMERKE: "Tjuveri av kjennemerke (registreringsskilt)",
          SKADEVERK: "Skadeverk på privat eigedom, gjenstandar eller køyretøy",
          KRENKELSE: "Ulovleg bruk av fast eigedom",
          BRUKSTYVERI: "Brukstjuveri av køyretøy eller båt frå privatperson",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      skjema: {
        SKADEVERK: {
          gjenstand: {
            label_zero: "Skadeverk",
            label_one:
              "Skadeverk på $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Skadeverk på fleire gjenstandar",
          },
        },
        TYVERI: {
          gjenstand: {
            label_zero: "Tjuveri",
            label_one:
              "Tjuveri av $t(anmeldelse.kort.skjema.gjenstandTyper.{{gjenstandType}})",
            label_other: "Tjuveri av fleire gjenstandar",
          },
        },
        gjenstandTyper: {
          ANNET: "Anna",
          FARTOY: "Fartøy",
          HANDELSVARER: "Gjenstandsliste",
          KJENNEMERKE: "Registreringsskilt (kjennemerke)",
          KJOERETOEY: "Køyretøy",
          MOBILTELEFON: "Mobiltelefon",
          SYKKEL: "Sykkel",
          ELEKTRONIKK: "Elektronikk",
          BYGNING_EIENDOM: "Bygning eller eigedom",
          VERKTOY: "Verktøy",
        },
      },
      og: "og",
    },
    nummer: "nummer",
    saksnummer: "Saksnummer",
    sendt_inn: "Sendt inn",
    sist_endret: "Sist endra",
    statusTyper: {
      UTKAST: "Utkast",
    },
  },
  auth: {
    min_profil: {
      ariaLabel: "Informasjon om din brukar",
      kontaktInfo: "Kontaktinformasjon til politiet",
      knapper: {
        logg_ut: "Logg ut",
      },
    },
    begrensetTilgang: {
      innlogget: {
        tittel: "Du har ikkje tilgang til å melde digitalt",
        isNotInnloggetMedGjeldendePid: {
          info: "Du er innlogga med ein utgått identifikator. Du må logga inn med identifikatoren som er gjeldande i folkeregisteret",
        },
      },
    },
    erDuHerFortsatt: {
      forbliKnapp: "Fortsett å vere innlogga",
      info: "Du har vore stille ei stund. Viss du ikkje gjer noko på denne sida, blir du snart logga ut.",
      loggUtKnapp: "Logg ut no",
      tittel: "Er du her framleis?",
    },
    idPortenBar: {
      kontaktPolitiet: "Kontakt politiet",
      loggetInnSom: "Logga inn som",
      nodnummer: "Nødnummer",
      ringPolitiet: "Ring politiet",
    },
    representasjon: {
      altinnFeil: "Kunne ikkje hente roller frå Altinn",
      fnr: "f. nr: {{fnr, fnr}}",
      ingenAaRepresenetere:
        "Basert på roller i Altinn kan du ikkje representere andre enn deg sjølv",
      megSelv: "(meg sjølv)",
      orgnr: "org. nr: {{orgnr, orgnr}}",
      velgHvemDuRepresenterer: "Vel kven du vil representere",
      virksomheter: "Verksemder",
    },
    utloggetSide: {
      automatiskInfo:
        "Du var stille ei stund. For å ivareta sikkerheita di, vart du automatisk logga ut. Meldinga er lagra, og du kan halda fram med å fylla den ut seinare",
      forsiden: "Gå til framsida",
      logginn: "Logg inn igjen",
      tittel: "Du har blitt logga ut",
    },
  },
  erstatningskrav: {
    beskrivelseErstatningskrav: {
      helptext: "Kva er tapet? Kvifor skal dette erstattas?",
      label: "Beskriv kva det krevjes erstatning for",
    },
    dokumentasjonVedlegg: {
      label:
        "Last opp dokumentasjon på at fornærma har rett til erstatning (valfritt)",
      label_ANMELDER:
        "Last opp dokumentasjon på at du har rett til erstatning (valfritt)",
      label_PERSON:
        "Last opp dokumentasjon på at fornærma har rett til erstatning (valfritt)",
      label_VIRKSOMHET:
        "Last opp dokumentasjon på at verksemda har rett til erstatning (valfritt)",
      helpText:
        "For eksempel kvitteringar av det som er tapt eller bilde av det som er øydelagt",
    },
    fullmaktVedlegg: {
      label_PERSON: `Last opp fullmakt til å reise erstatningskrav for fornærma (valfritt)`,
      label_VIRKSOMHET: `Last opp fullmakt til å reise erstatningskrav for verksemd (valfritt)`,
      helpText:
        "Verjar, eigarar, styreleiarar og liknande treng ikkje fullmakt",
    },
    infoErstatningskrav: {
      bulletpoints: [
        "Viss saka fører til bot eller rettssak, kan politiet krevja erstatning for ting som er stole eller øydelagt. Erstatninga går til personen eller verksemda som har hatt tapet. Dersom du får tapet ditt dekt av forsikringsselskapet, er det forsikringsselskapet som har krav på erstatning.",
        "For at me skal kunna krevja erstatning for deg må du dokumentera og forklara:",
        "BULLET_START",
        "Kva som er stole eller øydelagt",
        "Kva tinga er verde",
        "På kva måte tapet er knytt til hendinga",
        "BULLET_END",
        "Dokumentasjon kan til dømes vera kvitteringar, fakturaer, bilete eller prisoverslag på reparasjon.",
        "Om du melder for ein annan person eller verksemd må du ha ei fullmakt som viser at du har rett til å krevja erstatning på vegner av den fornærma. Ei fullmakt skal innehalda informasjon om kven som gir henne, kven som får henne og kva det blir fullmakt gitt til å gjera. Den bør vera datert og signert.",
      ],
    },
    ja: "Ja",
    nei: "Nei",
    radio1: "Ønsker fornærma å reise erstatningskrav?",
    radio1_ANMELDER: "Ønsker du å reise erstatningskrav?",
    radio1_PERSON: "Ønsker fornærma å reise erstatningskrav?",
    radio1_VIRKSOMHET: "Ønsker verksemda å reise erstatningskrav?",
    radioOptions: {
      JA: "Ja",
      NEI: "Nei",
      VET_IKKE: "Veit ikkje",
    },
    se_over: {
      dokumentasjonVedlegg:
        "Dokumentasjon på at fornærma har rett til erstatning",
      dokumentasjonVedlegg_ANMELDER:
        "Dokumentasjon på at du har rett til erstatning",
      dokumentasjonVedlegg_PERSON:
        "Dokumentasjon på at fornærma har rett til erstatning",
      dokumentasjonVedlegg_VIRKSOMHET:
        "Dokumentasjon på at verksemda har rett til erstatning",
      fullmaktVedlegg_PERSON: "Fullmakt til å reise erstatning for fornærma",
      fullmaktVedlegg_VIRKSOMHET:
        "Fullmakt til å reise erstatning for verksemda",
    },
    vet_ikke: "Veit ikkje",
  },
  filtrering: {
    filtreringsType: {
      kategori: {
        options: {
          INNBRUDD: {
            label: "Innbrot",
          },
          SKADEVERK: {
            label: "Skadeverk",
          },
          TYVERI: {
            label: "Tjuveri",
          },
          BEDRAGERI: {
            label: "Bedrageri",
          },
        },
        section: {
          label: "Kategori",
        },
      },
      tidsrom: {
        options: {
          FRA_DATO: {
            label: "Frå dato",
          },
          TIL_DATO: {
            label: "Til dato",
          },
        },
        section: {
          label: "Tidsrom",
        },
      },
    },
    mobilknapp: {
      label: "Filtrer",
      labelMedFilterOne: "{{antall}} aktivt filter",
      labelMedFilterOther: "{{antall}} aktive filter",
    },
  },
  forside: {
    tittel: "Melde digitalt",
    spraakvelger: {
      ariaLabel: "Velg språk",
    },
    tabContainer: {
      anmelde: "Melde",
      utkast: "Utkast",
      innsendt: "Innsend",
    },
    info: {
      title: "Informasjon og rettleiing",
      tipsUtfylling: {
        title: "Tips til deg som skal fylle ut ei melding",
        bulletpoints: [
          "Ha gjerne dokumentasjon og vedlegg klare før du startar. Eksempler på dokumentasjon kan vere videoovervaking, bilete av stolne gjenstandar, kvitteringar, kontoutskrifter eller skjermbilete av kommunikasjon mellom deg og gjerningspersonen.",
          "Meldinga består av 8 steg, og du kan fylle inn i den rekkefølga du ønsker. Det kan vere lurt å bla igjennom alle stega før du begynner slik at du forstår kva for opplysningar som passar kor.",
          "For at politiet skal kunne reagere på lovbrotet, er det viktig med gode skildringar av hendinga og så mange opplysningar som mogleg.",
          "Noen opplysningar vil vera viktige å fylla inn sjølv om dei ikkje er påkravde, eksempelvis serienummer på stolne gjenstandar og fødselsnummer på involverte personar viss du har dette.",
          "Dersom fleire gjerningspersonar er involverte i same lovbrot, skal det berre leverast ei melding. Det er då særleg viktig med god skildring av kva kvar enkelt har gjort og kvifor hen er mistenkt for det aktuelle lovbrotet.",
        ],
        veileder: {
          lenke: "Les meir i rettleiar for digital melding",
        },
      },
      nyeOpplysningerOmAnmeldtForhold: {
        title: "Nye opplysningar om meldt forhold",
        tekst: [
          "Dersom du ønsker å trekkje tilbake meldinga eller gi oss fleire opplysningar om saka,",
          "kan du kontakte politidistriktet der hendinga skjedde og opplyse om saksnummeret",
          "du fekk då du sende inn meldinga. Då kan vi oppdatere eller trekkje tilbake",
          "meldinga di.",
        ],
        veileder: {
          lenke: "Les meir om ettersending og finn rett politistasjon",
        },
      },
    },
    beskrivelse: {
      omAaAnmelde: {
        infotekst: {
          info: "I meldingsportalen kan du melda lovbrota tjuveri, skadeverk, innbrot, bedrageri og datakriminalitet digitalt. Du kan også sjå dine innsende meldingar eller halda fram på meldingar du har byrja å fylla ut.",
          andreKrimTyper:
            "Skal du melde andre typar kriminalitet, må du kontakta ditt næraste politidistrikt.",
        },
      },
      dineAnmeldelser: {
        tittel: "Meldingane dine",
        innsendteAnmeldelser: {
          tittel: "Innsende meldingar",
          badRequest:
            "Tilgangskontroll feila. Det er ikkje opna for å sjå innsende meldingar på vegner av andre personar.",
          info: "Her blir meldingar som du har sendt inn på politiet.no viste. Viss du har spørsmål om meldingar som vart leverte på andre måtar, ber vi deg ta kontakt med næraste politi.",
          infoRepresentasjon:
            "Her blir meldingar som er sendt inn på vegner av verksemda på politiet.no viste. Viss du har spørsmål om meldingar som vart leverte på andre måtar, ber vi deg ta kontakt med næraste politi.",
          infoTom:
            "Her er det ingen meldingar. Viss du leitar etter ei melding du har sendt inn tidlegare, ber vi deg kontakte næraste politi.",
          manglerTilgang:
            "Du har ikkje tilgang til å sjå innsende meldingar på vegner av {{ valgtPartId }}. Dagleg leiar skal ha tilgang, og kan også delegere tilgangen til andre i Altinn.",
        },
        utkast: {
          tittel: "Dine utkast",
          info: "Her er meldingar som du har starta på, men som ikkje er sendt inn ennå.",
          tomt: "Du har inga påbyrja meldingar.",
        },
      },
    },
    knapper: {
      opprettAnmeldelse: {
        tyveriSkadeverkInnbrudd: "Meld tjuveri, innbrot og skadeverk",
        bedrageri: "Meld bedrageri (svindel) og datakriminalitet",
      },
      loggInn: "Logg inn",
    },
    eksisterendeAnmeldelseUtkast: {
      modal: {
        tittel: "Vil du halda fram på utkastet ditt?",
        beskrivelse:
          "Du har allereie byrja på ei melding av {{anmeldelseType}}. Ønskjer du å halda fram på den?",
        fortsettUtfylling: "Hald fram utfylling",
        opprettNy: "Opprett ny melding",
      },
    },
  },
  hvaHarSkjedd: {
    felles: {
      gjenstander: {
        forsikring: {
          title: "Forsikringsinformasjon",
          radioSpoersmaal: "Skal gjenstanden erstattast av forsikring?",
          radioOptions: {
            JA: "Ja",
            NEI: "Nei",
          },
          polisenummer: {
            label: "Forsikringsnummer (polisenummer)",
          },
          forsikringsselskap: {
            se_over_label: "Forsikringsselskap",
          },
          sok: {
            label: "Forsikringsselskap",
            helpText:
              "Søk opp namn eller organisasjonsnummer. Undersøk forsikringsavtalen din om du er usikker på kven som er leverandør.",
            feilmeldingBrreg:
              "Kunne ikkje hente forsikringsselskap frå Brønnøysundregistrene",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet treng vi eit identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjere etterlysing mogleg.",
            label: "Identifiserande nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalspris eller omtrentlig pris i heile kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annan type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seglbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Vel type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
            options: {
              "01": "Kvit",
              "02": "Rød",
              "03": "Blå",
              "04": "Grønn",
              "05": "Grå",
              "06": "Svart",
              "07": "Beige",
              "08": "Brun",
              "09": "Gul",
              "10": "Oransje",
              "11": "Sølv",
              "12": "Gull",
              "13": "Rosa",
              "14": "Fiolett",
              "29": "Turkis",
              "99": "Anna",
            } satisfies Record<SVVFargekode, string>,
          },
          knapp: {
            manglerNorskRegNr: "Manglar norsk registreringsnummer",
            manglerRegNr: "Manglar norsk registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            regnr: "Registreringsnummer",
            slett: "Slett køyretøy",
            utenlandskRegNr: "Utanlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fekk ikkje kontakt med Statens vegvesen, prøv igjen seinare.",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørringsglaset for å søkje",
            labelUtenlandsk: "Utanlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må vera mellom 2 og 7 teikn",
          },
          type: {
            ANNET: "Anna",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhengar",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Vel type køyretøy",
            seOverLabel: "Type køyretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
      },
      gjenstandTyper: {
        ANNET: "Anna",
        FARTOY: "Båt, vannscooter eller andre fartøy",
        HANDELSVARER: "Gjenstandsliste",
        KJENNEMERKE: "Registreringsskilt (kjennemerke)",
        KJOERETOEY: "Bil, moped eller andre køyretøy",
        MOBILTELEFON: "Mobiltelefon",
        SYKKEL: "Sykkel eller sparkesykkel",
        ELEKTRONIKK: "Elektronikk",
        BYGNING_EIENDOM: "Bygning eller eigedom",
        VERKTOY: "Verktøy",
      },
      modal: {
        fullfoer: "Fullfør",
        lukk: "Lukk",
      },
      panel: {
        redigerAriaLabel: "Endre gjenstand {{nr}}",
        gjenstandTypeIkkeValg: "Gjenstand",
        slett: "Slett",
        ukjentGjenstandType: "Gjenstandtype manglar",
      },
    },
    bedrageri: {
      tittel: "Bedrageri",
      hvordanStartetBedrageriet: {
        title: "Korleis starta det?",
        input: {
          initiellKontakt: {
            label:
              "Korleis kom du først i kontakt med svindlaren eller svindlarane?",
            annet: {
              label: "Beskriv korleis du først kom i kontakt",
            },
            options: {
              MELDING_SMS: "Eg fekk ei melding eller SMS",
              E_POST: "Eg fekk ein e-post",
              OPPRINGT: "Eg blei oppringt",
              KJOEP_SALG: "På ei teneste for kjøp og sal",
              SOSIALE_MEDIER: "På sosiale medium",
              ANNET: "Anna",
            },
          },
        },
      },
      hvaSkjedde: {
        title: "Kva skjedde?",
        options: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<BedrageriMetodeTypeSvar, string>,
        input: {
          beskrivelse: {
            label: "Beskriv heile hendinga",
          },
          moettSvindlerFysisk: {
            label: "Møtte du svindlaren eller svindlarane fysisk?",
          },
          harDuMottattLenke: {
            label: "Har du teke imot ei lenkje i samband med svindelen?",
          },
          lenke: {
            label: "Lim inn lenkja her",
          },
          harInstallertProgramEllerApp: {
            label:
              "Har du i samband med hendinga installert eit program eller ein app på ein av einingane dine?",
          },
          hvilketProgramEllerApp: {
            label: "Noko som program eller app vart installert?",
          },
          paaHvilkenEnhet: {
            label: "På kva eining vart programmet eller appen installert?",
            options: {
              TELEFON: "Telefon",
              PC: "PC",
              NETTBRETT: "Nettbrett",
            },
          },
        },
      },
      tilegnedeVerdier: {
        title: "Kva har du mista?",
        panel: {
          redigerAriaLabel: "Endre {{type}} {{nr}}",
        },
        options: {
          PENGER: "Pengar",
          PERSONINFORMASJON: "Personinformasjon",
          KODE: "Kode",
          PASSORD: "Passord",
          FYSISK_ID: "Fysisk ID",
          ANNET: "Anna",
        } satisfies Record<TilegnetVerdiType, string>,
        input: {
          tilegnetVerdi: {
            label: "Kva har svindlaren eller svindlarane fått tak i?",
            helpText: "Vel alle relevante alternativ",
          },
        },
        PENGER: {
          title: "Transaksjon",
          fjern: "Slett",
          modalTitle: "Skildring av transaksjon",
          info: "Vel kortbetaling om du har brukt kort til å betale i falske nettbutikkar eller andre falske nettsider eller dersom det er trekk på kontoen din med ukjent opphav.",
          knapper: {
            leggTil: "Legg til transaksjon",
            leggTilFlere: "Legg til fleire transaksjonar",
          },
          fratattSum: {
            label: "Kor mykje fekk svindlaren eller svindlarane tak i?",
            seOverLabel: "Sum",
            helpText: "Oppgi summen i heile NOK",
            totalSum: "Totalsum stolne pengar (NOK)",
          },
          overforing: {
            dropdown: {
              label: "Korleis fekk svindlaren eller svindlarane tak i pengane?",
            },
            options: {
              KONTOOVERFORING: "Kontooverføring",
              VIPPS: "Vipps-overføring",
              KRYPTO: "Kryptotransaksjon",
              KORTBETALING: "Kortbetaling",
              ANNET: "Anna",
            } satisfies Record<OverforingType, string>,
            KONTOOVERFORING: {
              fraKontonummer: {
                label: "Frå kva konto vart pengane overført?",
                seOverLabel: "Frå konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              tilKontonummer: {
                label: "Til kva konto vart pengane overført? (valfritt)",
                seOverLabel: "Til konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraBank: {
                label: "I kva bank er denne kontoen?",
                seOverLabel: "Bank",
              },
            },
            KORTBETALING: {
              kortnummer: {
                infoboks:
                  "Kortnummer og kontonummer er viktig informasjon om me skal etterforska hendinga. Det er derfor viktig at du oppgir desse opplysningane om du har dei.",
                label: "Kva kort vart brukt i betalinga? (valfritt)",
                seOverLabel: "Kortnummer",
                helpText: "Oppgi kortnummer",
              },
              fraKontonummer: {
                label: "Frå kva konto vart pengane overførte? (valfritt)",
                seOverLabel: "Frå konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraBank: {
                label: "I kva bank er denne kontoen?",
                seOverLabel: "Bank",
              },
              mottaker: {
                label: "Til kva mottakar vart pengane betalte?",
                seOverLabel: "Til mottaker",
                helpText: "Til dømes namn på person eller verksemd",
              },
            },
            VIPPSOVERFORING: {
              fraKontonummer: {
                label: "Frå kva konto vart pengane overført?(valfritt)",
                seOverLabel: "Frå konto",
                helpText: "Oppgi kontonummer, IBAN eller BIC",
              },
              fraTelefonnummer: {
                label: "Frå kva telefonnummer vart pengane overførte?",
                seOverLabel: "Frå telefonnummer",
              },
              tilTelefonnummer: {
                label:
                  "Til kva telefonnummer eller Vippsnummer vart pengane overførte?",
                seOverLabel: "Til telefonnummer / Vippsnummer",
              },
              fraBank: {
                label: "I kva bank er denne kontoen?",
                seOverLabel: "Bank",
              },
              mottakernavn: {
                label: "Namn på mottakar",
                seOverLabel: "Mottakar",
              },
            },
            KRYPTO: {
              belop: {
                label: "Kva var beløpet i kryptovaluta?",
                helpText: "Oppgi beløpet i gjeldande kryptovaluta",
              },
              valuta: {
                label: "Oppgi kryptovaluta",
                helpText: "Til dømes Bitcoin, Ethereum, XRP",
              },
              fraAdresse: {
                label: "Frå kva kryptovalutaadresse vart pengane tekne?",
                seOverLabel: "Frå kryptovalutaadresse",
                helpText: "Frå kryptovaluta-adresse",
              },
              tilAdresse: {
                label: "Kva kryptovalutaadresse gjekk pengane til? (valfritt)",
                seOverLabel: "Til kryptovaluta-adresse",
              },
              vekslingstjeneste: {
                label: "Kva vekslingsteneste blei nytta?",
                helpText: "Oppgi kryptovalutabørsen som vart brukt",
                seOverLabel: "Vekslingsteneste",
              },
              transaksjonsId: {
                label: "Transaksjons-ID (hash) på overføringa",
                seOverLabel: "Transaksjons-ID",
              },
            },
            ANNET: {
              beskrivelse: {
                label: "Beskriv korleis pengane vart teke og kvar dei gjekk",
                seOverLabel: "Skildring",
                helpText:
                  "Til dømes uttak i namngitt minibank eller bruk i namngitt butikk",
              },
            },
          },
          selvOverfort: {
            harDuSelvOverfortPengene: {
              options: {
                JA: "Ja",
                NEI: "Nei",
              } satisfies Record<BedrageriMetodeTypeSvar, string>,
              label:
                "Har du sjølv overført pengar til svindlaren eller svindlarane?",
            },
            overforingFormaal: {
              label: "Kva var formålet med overføringa?",
              options: {
                INVESTERING: "Investering",
                VARER_TJENESTER: "Kjøp av varer eller tenester",
                GAVE: "Gåve",
                UTLAAN: "Utlån",
                FAKTURA: "Faktura (falsk)",
                UTPRESSING: "Utpressing",
                ABONNEMENT: "Abonnement",
                GEBYR: "Gebyr",
                ANNET: "Anna",
              } satisfies Record<OverforingFormaalType, string>,
            },
            annetOverforingFormaalBeskrivelse: {
              label: "Forklar kva formålet med overføringa var",
            },
            erOverforingGodkjentMedPassordEtc: {
              label:
                "Har du godkjent overføringar ved å taste kode eller passord?",
              options: {
                JA: "Ja",
                NEI: "Nei",
              } satisfies Record<BedrageriMetodeTypeSvar, string>,
            },
          },
          tilbakeforing: {
            type: {
              label: "Har du fått pengane tilbake?",
              options: {
                JA: "Ja",
                NEI: "Nei",
                DELVIS: "Delvis",
              } satisfies Record<TilbakeforingType, string>,
            },
            hvemHarFornaermedeFaattPengerFra: {
              label: "Kven har du fått pengane tilbake frå?",
            },
            hvorMyeHarFornaermedeFaattTilbake: {
              label: "Kor mykje har du fått tilbake?",
              helpText: "Oppgi beløpet i NOK",
            },
          },
          bankenStansetOverforing: {
            overforingStanset: {
              label: "Har banken stansa pengane?",
            },
            hvorMyeHarBankenStanset: {
              label: "Kor mye har banken stansa?",
              helpText: "Oppgi beløpet i NOK",
            },
            optionsJaNeiDelvis: {
              JA: "Ja",
              NEI: "Nei",
              DELVIS: "Delvis",
            } satisfies Record<HarBankenStansetOverforingType, string>,
          },
        },
        PERSONINFORMASJON: {
          checkbox: {
            label:
              "Kva for personopplysningar har svindlaren eller svindlarane fått tak i?",
            options: {
              NAVN: "Namn",
              ADRESSE: "Adresse",
              TELEFONNUMMER: "Telefonnummer",
              FOEDSELSNUMMER_ELLER_DNUMMER: "Fødselsnummer / D-nummer",
              ANNET: "Anna personinformasjon",
            } satisfies Record<TilegnetPersoninformasjonVerdiType, string>,
            extraInput: {
              ANNET: {
                label: "Forklar kva for personopplysningar",
              },
            },
          },
        },
        KODE: {
          beskrivelse: {
            label: "Kva var koden til?",
          },
        },
        PASSORD: {
          beskrivelse: {
            label: "Kva var passordet til?",
          },
        },
        FYSISK_ID: {
          checkbox: {
            label:
              "Kva slags fysisk ID har svindleren eller svindlerne fått tak i?",
            options: {
              PASS: "Pass",
              NASJONALT_ID_KORT: "Nasjonalt ID-kort",
              FORERKORT: "Førarkort",
              BANKKORT_MED_BILDE: "Bankkort med bilete",
              ANNET: "Anna",
            } satisfies Record<TilegnetVerdiFysiskIdType, string>,
            extraInput: {
              ANNET: {
                label: "Forklar kva slags fysisk ID",
              },
            },
          },
        },
        ANNET: {
          beskrivelse: {
            label: "Beskriv det svindlarane har tatt",
            helpText: "Kva er det? Er det levert tilbake? Antall?",
          },
          sum: {
            label: "Verdi av det som er tatt (NOK)",
            helpText: "Oppgi utsalspris eller omtrentlig pris i heile kroner.",
          },
        },
      },
      hvaSkjeddeEtter: {
        title: "Kva skjedde etterpå?",
        optionsJaNei: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<EgneTiltakType, string>,
        input: {
          vetBanken: {
            label: "Veit banken din kva som har skjedd?",
          },
          kortSperret: {
            label: "Er kortet ditt sperra?",
          },
          kontoSperret: {
            label: "Er kontoen din sperra?",
          },
        },
      },
    },
    innbrudd: {
      beskrivelse: {
        helpText: "For eksempel bod, bil, lager",
        label: "Kva var det innbrot i?",
      },
      se_over: {
        beskrivelse: "Skildring",
        label: "Kva var det innbrot i?",
      },
      tittel: "Innbrot",
    },
    options: {
      INNBRUDD: { label: "Nokon har brote seg inn ein stad (innbrot)" },
      SKADEVERK: { label: "Nokon har øydelagt noko (skadeverk)" },
      TYVERI: { label: "Nokon har stole noko (tjuveri)" },
      BEDRAGERI: {
        label: "Bedrageri (svindel)",
        helpText:
          "Nokon har lurt eller forsøkt å lure til seg pengar eller verdiar dei ikkje har rett til",
      },
      DATAKRIM: {
        label: "Datakriminalitet",
        helpText:
          "Nokon har ulovleg teke seg inn i eit datasystem og stole, endra eller øydelagt data",
      },
    } satisfies Record<HandlingType, { label: string; helpText?: string }>,
    datakrim: {
      tittel: "Datakriminalitet",
      infoboks_sikkert_dot_no:
        "På <anchorlink>sikkert.no</anchorlink> får du fleire råd frå myndigheitene om å handtere ei uønskt digital hending, eller forhindre at det skjer i framtida.",
      datakrimType: {
        checkbox: {
          label: "Kva beskriv best det som har skjedd?",
          helpText: "Kryss av for det som passar (fleire val mulig)",
          extraInput: {
            ANNET: {
              label: "Beskriv kort kva som har skjedd",
              se_over: "Beskriv kort kva som har skjedd (anna)",
            },
          },
          options: {
            DATAINNBRUDD: {
              label: "Datainnbrudd",
              helpText:
                "nokon har skaffa seg urettmessig tilgang til eit datasystem",
            },
            VIRUS: {
              label: "Løysepengarvirus eller skadevare",
              helpText:
                "programvare som har låst eller kryptert innhold i eit datasystem",
            },
            DOS_DDOS: {
              label: "Tenestenektangrep (DoS)",
              helpText:
                "angrep som gjer informasjon, ressursar eller tenester heilt eller delvis utilgjengelege",
            },
            KJOP_SALG_INNLOGGINGSINFORMASJON: {
              label: "Kjøp og sal av påloggingsinformasjon",
            },
            ID_TYVERI: { label: "ID-tyveri eller kontokapring" },
            ANNET: { label: "Anna" },
          } satisfies Record<
            DatakrimType,
            { label: string; helpText?: string }
          >,
        },
      },
      dataPaAvveie: {
        label: "Er det grunn til å tro at data er på avvegar?",
        beskrivelse: {
          label: "Forklar kvifor",
        },
      },
      andreMyndigheterVarslet: {
        label:
          "Er andre myndigheiter eller verksemder blitt varsla eller involvert i håndteringa av denne hendelsen?",
        beskrivelse: {
          label: "Kva for myndigheiter eller verksemder er involvert?",
        },
      },
      datakrimSvarType: {
        JA: "Ja",
        NEI: "Nei",
      } satisfies Record<DatakrimSvarType, string>,
      tilleggsinformasjon: {
        label: "Informasjon om hendelsen eller dei som står bak (valfritt)",
        helpText:
          "For eksempel IP-adresser, domene, hasher, filnamn, e-postadresser, kryptovaluttaadresser eller namn",
      },
    },
    skadeverk: {
      beskrivelse: {
        helpText: "For eksempel kva som er skada eller korleis det har skjedd",
        label: "Beskriv skadeverket",
      },
      formuestap: {
        helpText: "Dersom du er usikker, kan du oppgi omtrentleg pris",
        label: "Pris på det som er øydelagt (NOK)",
      },
      gjenstander: {
        bygningEiendom: {
          beskrivelse: {
            helpText:
              "Kva del av av eigedomen er det? Korleis ble det øydelagt?",
            label: "Beskriv det som er øydelagt",
          },
          panel: {
            beskrivelse: {
              label: "Skildring",
            },
            pris: "Pris",
          },
        },
        annenGjenstand: {
          beskrivelse: {
            helpText: "Kva er det? Korleis ble det øydelagt?",
            label: "Beskriv det som er øydelagt",
          },
          panel: {
            beskrivelse: {
              label: "Skildring",
            },
            pris: "Pris",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet treng vi eit identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjere etterlysing mogleg.",
            label: "Identifiserande nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalspris eller omtrentlig pris i heile kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annan type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seglbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Type fartøy",
          typeLabel: "Type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
          },
          knapp: {
            manglerNorskRegNr: "Manglar norsk registreringsnummer",
            manglerRegNr: "Manglar norsk registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            slett: "Slett køyretøy",
            utenlandskRegNr: "Utanlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fekk ikkje kontakt med Statens vegvesen, prøv igjen seinare.",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørringsglaset for å søkje",
            labelUtenlandsk: "Utanlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må vera mellom 2 og 7 teikn",
          },
          type: {
            ANNET: "Anna",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhengar",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Vel type køyretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
        sum: "Sum øydelagte gjenstandar (NOK)",
      },
      leggTilFlereGjenstander: "Legg til fleire øydelagde gjenstandar",
      leggTilGjenstand: "Legg til øydelagd gjenstand",
      modal: {
        dropdownLabel: "Kva er øydelagt?",
        tittel: "Skildring av øydelagd gjenstand",
      },
      panel: {
        beskrivelse: {
          label: "Skildring av skaden",
        },
        dropdownLabel: "Kva er øydelagt?",
        formuestap: {
          label: "Pris",
        },
      },
      tittel: "Skadeverk",
    },
    tyveri: {
      beskrivelse: {
        helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
        label: "Spesielle kjennetegn",
      },
      gjenstander: {
        annenGjenstand: {
          beskrivelse: {
            helpText: "Kva er det? Er det levert tilbake? Antal?",
            label: "Beskriv det som er stole",
          },
          panel: {
            beskrivelse: {
              label: "Skildring",
            },
            pris: "Pris",
          },
        },
        fartoy: {
          beskrivelse: {
            helpText: "For eksempel skade, farge, klistremerke, tilleggsutstyr",
            label: "Spesielle kjennetegn",
          },
          idnr: {
            helpText:
              "For å kunne etterlyse fartøyet treng vi eit identifiserende nummer. Fyll derfor ut enten registreringsnummer eller skrognummer (WIN) dersom du ønsker å gjere etterlysing mogleg.",
            label: "Identifiserande nummer",
          },
          lengde: {
            label: "Lengde (fot)",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          nasjonalitet: {
            label: "Nasjonalitet",
          },
          pris: {
            helpText: "Oppgi utsalspris eller omtrentlig pris i heile kroner.",
            label: "Pris (NOK)",
          },
          registreringsnummer: {
            helpText: "For eksempel serienummer, seilnummer",
            label: "Registreringsnummer",
          },
          skrogtype: {
            ENKELT_SKROG: "Enkelt skrog",
            KATAMARAN: "Katamaran",
            TRIMARAN: "Trimaran",
          },
          type: {
            ANNET: "Annan type båt",
            GUMMIBAAT: "Gummibåt",
            JOLLE_ROBAAT: "Jolle/Robåt",
            KANO_KAJAKK: "Kano/Kajakk",
            MOTORBAAT: "Motorbåt",
            RIB: "RIB (Rigid Inflatable Boat)",
            SEILBAAT: "Seglbåt",
            SKOEYTE: "Skøyte",
            SNEKKE: "Snekke",
            VANNSCOOTER: "Vannscooter",
          },
          typeFartoy: "Type fartøy",
          typeLabel: "Type fartøy",
          typeSkrog: "Skrogtype",
          win: {
            helpText: "Eventuelt CIN eller HIN",
            label: "WIN-kode",
          },
        },
        handelsvarer: {
          caption: {
            label: "Gjenstandsliste",
          },
          antall: {
            label: "Antal",
          },
          leggTilFlereVarer: "Legg til fleire gjenstandar",
          leggTilVare: "Legg til gjenstand",
          slett: {
            ariaLabel: "Slett gjenstand",
          },
          navn: {
            label: "Gjenstandstype og merke",
          },
          pris: {
            label: "Pris per stykk (NOK)",
          },
          total: {
            label: "Sum gjenstandar (NOK)",
          },
        },
        kjennemerke: {
          kategori: {
            label: "Kjennemerkekategori",
            options: {
              KJORETOY: "Hovudkjennemerke",
              NORMAL: "Normal",
              PERSONLIG: "Personleg kjennemerke",
              PROVE: "Prøvekjennemerke",
            } satisfies Record<KjennemerkeKategori, string>,
          },
          type: {
            label: "Kjennemerketype",
            options: {
              AMBASSADE: "Ambassadekjennemerke (Gule teikn på blå botn)",
              ARSPROVEKJENNEMERKE:
                "Årsprøvekjennemerke (Kvite teikn på raud botn)",
              ARSPROVEKJENNEMERKE_T:
                "Årsprøvekjennemerke Tilhenger (Kvite teikn på raud botn med T)",
              DAGSPROVEKJENNEMERKE:
                "Dagsprøvekjennemerke (Svarte teikn på raud botn)",
              ORDINART: "Ordinære kjennemerke (Svarte teikn på kvit botn)",
              RALLY_TRIAL_ENDURO:
                "Rallykjennemerke (Kvite teikn på svart botn)",
              SVALBARD: "Svalbard-kjennemerke (Gule teikn på svart botn)",
              UTENFOR_OFFENTLIG_VEG:
                "Utanfor offentleg veg (Gule teikn på svart botn)",
              VAREBIL_KLASSE_2: "Varebil klasse 2 (Svarte teikn på grøn botn)",
              VETERAN: "Veterankjennemerke",
              VETERAN_UTENFOR_OFFENTLIG_VEI:
                "Veteran utanfor offentleg veg (Gule teikn på svart botn)",
            } satisfies Record<KjennemerketypeType, string>,
          },
          antallSkilt: {
            label: "Kor mange skilt er stolne?",
          },
          panel: {
            antallSkilt: "Antal stolne skilt",
            registreringsnummer: "Registreringsnummer (kjennemerke)",
          },
          registreringsnummer: {
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørringsglaset for å søkje",
          },
          alert: {
            header: "Er registreringsskiltet mista?",
            body: "Då skal det ikkje meldast som stole. I staden må du fylla ut  <0>skjema om tap av kjennemerke</0>.Ferdig utfylt skjema må signerast og stemplast på ein politistasjon",
          },
        },
        kjoretoy: {
          aarsmodell: {
            antSiffer: "(4 siffer)",
            label: "Årsmodell",
          },
          farge: {
            label: "Farge",
          },
          knapp: {
            manglerNorskRegNr: "Manglar norsk registreringsnummer",
            manglerRegNr: "Manglar norsk registreringsnummer",
            norskRegNr: "Søk på norsk registreringsnummer",
            slett: "Slett køyretøy",
            utenlandskRegNr: "Utanlandsk registreringsnummer",
          },
          merke: {
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          registreringsland: "Nasjonalitet",
          registreringsnummer: {
            feilmeldingSvv:
              "Fekk ikkje kontakt med Statens vegvesen, prøv igjen seinare.",
            label: "Legg til registreringsnummer (kjennemerke)",
            helpText:
              "Skriv registreringsnummer og trykk enter eller på forstørringsglaset for å søkje",
            labelUtenlandsk: "Utanlandsk registreringsnummer (kjennemerke)",
            panelLabel: "Registreringsnummer (kjennemerke)",
            ugyldig: "Registreringsnummeret må vera mellom 2 og 7 teikn",
          },
          type: {
            ANNET: "Anna",
            BUSS: "Buss",
            BELTEBIL: "Beltebil",
            CAMPINGBIL: "Campingbil",
            CAMPINGVOGN: "Campingvogn",
            KOMBINERT_BIL: "Kombinert bil",
            LASTEBIL: "Lastebil",
            MOPED: "Moped",
            MOTORSYKKEL: "Motorsykkel",
            PERSONBIL: "Personbil",
            SNOSCOOTER: "Snøscooter",
            TILHENGER: "Tilhengar",
            TRAKTOR: "Traktor",
            TREKKVOGN_ELLER_TREKKBIL: "Trekkvogn/-bil",
            VAREBIL: "Varebil",
          },
          typeKjoretoy: {
            label: "Type køyretøy",
          },
          understellsnummer: {
            label: "Understellsnummer (VIN)",
          },
        },
        mobiltelefon: {
          imei: {
            label: "IMEI-nummer",
            helpText: "15 siffer",
          },
          merke: {
            helpText: "For eksempel Apple, Samsung",
            label: "Merke",
          },
          modell: {
            helpText: "For eksempel iPhone 14 Pro, Galaxy A25",
            label: "Modell",
          },
          pris: {
            label: "Pris",
          },
          telefonnummer: {
            label: "Telefonnummer",
          },
          teleselskap: {
            helpText: "For eksempel Telenor, Telia",
            label: "Teleselskap",
          },
        },
        sykkel: {
          merke: {
            helpText: "For eksempel DBS, Merida, Scott",
            label: "Merke",
          },
          modell: {
            label: "Modell",
          },
          rammenummer: {
            helpText: "Oppgi rammenummer dersom du har det",
            label: "Rammenummer/serienummer",
          },
          type: {
            ANNEN_TYPE_SYKKEL: "Annan type sykkel",
            BARNESYKKEL: "Barnesykkel",
            BYSYKKEL: "Bysykkel",
            CYCLOCROSS_GRAVEL: "Cyclocross / Gravel",
            EL_SPARKESYKKEL: "Elsparkesykkel",
            EL_SYKKEL: "Elsykkel",
            HYBRIDSYKKEL: "Hybridsykkel",
            LANDEVEISSYKKEL: "Landevegssykkel",
            SPARKESYKKEL: "Sparkesykkel",
            TERRENGSYKKEL: "Terrengsykkel",
          },
          typeSykkel: "Vel type sykkel",
        },
        elektronikk: {
          type: {
            NETTBRETT: "Nettbrett",
            DATAMASKIN: "Datamaskin",
            SPILLKONSOLL: "Spelkonsoll",
            SMARTKLOKKE: "Smartklokke",
            TV: "TV",
            HODETELEFONER: "Hovudtelefonar",
            HVITEVARER: "Kvitevarer",
            STØVSUGER: "Støvsugar",
            ANNET: "Anna",
          } satisfies Record<ElektronikkType, string>,
          typeElektronikk: "Vel type elektronikk",
          merke: {
            label: "Merke",
            helpText: "Til dømes Samsung, Playstation, Apple,",
          },
          modell: {
            label: "Modell",
            helpText: "Til dømes Galaxy buds, PS5, MacBook Pro",
          },
          serienummer: {
            label: "Serienummer (valfritt)",
          },
          pris: {
            label: "Pris",
          },
        },
        verktoy: {
          type: {
            label: "Type verktøy",
            helpText: "Til dømes drill, sag, vinkelslipar",
          },
          merke: {
            label: "Merke",
            helpText: "For eksempel Bosch, Ryobi, DeWalt",
          },
          modell: {
            label: "Modell",
          },
          serienummer: {
            label: "Serienummer (valfritt)",
          },
        },
        sum: "Totalpris stolne gjenstandar (NOK)",
      },
      leggTilFlereGjenstander: "Legg til fleire stolne gjenstandar",
      leggTilGjenstand: "Legg til stolen gjenstand",
      modal: {
        dropdownLabel: "Kva er stole?",
        tittel: "Skildring av stolen gjenstand",
      },
      panel: {
        beskrivelse: {
          label: "Spesielle kjennetegn",
        },
        dropdownLabel: "Kva er stole?",
        pris: {
          label: "Pris",
        },
      },
      pris: {
        helpText: "Oppgi utsalspris eller omtrentlig pris i heile kroner.",
        label: "Pris på det som er stole (NOK)",
      },
      tittel: "Tjuveri",
    },
  },
  hvemGjordeDet: {
    navbar: "Gjerningsperson stegveljar",
    gjerningspersoninformasjonSvarType: {
      KJENT: "Ja",
      UKJENT: "Nei, eg veit ikkje kven det er",
    },
    input: {
      beskrivelse: "Andre opplysningar om gjerningspersonen",
      beskrivelseHelpText:
        "For eksempel kjønn, alder, hårfarge, alder, høgde, fasong, klede, språk, dialekt, arr, osv.",
      etternavn: "Etternamn",
      fornavn: "Fornamn",
      telefon: "Telefonnummer",
      vergeLabel: "Kontaktopplysningar til verje",
    },
    leggTilGjerningsvirksomhet: "Legg til verksemd",
    leggTilFlereGjerningsperson: "Legg til fleire gjerningspersonar",
    leggTilGjerningsperson: "Legg til gjerningsperson",
    modal: {
      tittel: "Om gjerningspersonen",
      forrige: "Førre",
      neste: "Neste",
      fullfoer: "Fullfør",
      lukk: "Lukk",
      avbryt: "Avbryt",
      fornavn: "Oppgitt fornamn",
      etternavn: "Oppgitt etternamn",
      epost: "E-postadresse",
      telefon: "Telefonnummer",
      beskrivelse: "Andre opplysningar",
    },
    panel: {
      antattGjerningsperson: "Antatt gjerningsperson",
      gjerningspersonNr: "Gjerningsperson {{nr}}",
      slett: "Slett gjerningsperson",
      redigerAriaLabel: "Endre gjerningsperson {{nr}}",
    },
    gjerningsperson: {
      details: {
        gjerningspersonNr: "Gjerningsperson {{nr}}",
        label: "Legg til éin eller fleire typar opplysningar",
        alder: {
          gjerningspersonAlderSvarType: {
            OVER_ATTEN: "Over 18 år",
            UNDER_ATTEN: "Under 18 år",
            UNDER_FEMTEN: "Under 15 år",
            VET_IKKE: "Veit ikkje",
          },
          label: "Kor gammal er gjerningspersonen?",
          title: "Alder",
          vergeInfo:
            "Personen du har lagt til er under 18 år. Har du opplysningar om verje eller føresett? Då kan du leggja dei til med eigen knapp.",
        },
        begrunnelseForInvolvering: {
          label: "Kvifor meiner du at denne personen har gjort det?",
        },
        slett: "Slett gjerningsperson",
      },
      panel: {
        endre: {
          label: "Endre",
        },
        slett: {
          label: "Slett",
        },
      },
      fragments: {
        personopplysninger: {
          leggTil: "Legg til namn eller andre personopplysningar",
          tittel: "Personopplysningar",
          helptext:
            "Du må oppgi <strong>minst éi</strong> personopplysning, men gjerne så mange du kan",
          fornavn: "Fornamn",
          etternavn: "Etternamn",
          foedselsEllerDnummer: {
            label: "Personleg ID (11 siffer)",
            helpText: "Fødselsnummer / D-nummer",
          },
          foedselsdato: {
            label: "Fødselsdato",
            helpText: "dd.mm.åååå",
          },
          identifiseringsinformasjon: {
            beskrivelse: {
              ANNET: {
                label: "Beskriv korleis du har identifisert personen",
              },
              FREMVIST_ID: {
                label: "Kva slags ID er vist fram?",
              },
            },
            label: "Korleis er personen identifisert?",
            options: {
              ANNET: "Anna",
              FREMVIST_ID: "Framvist ID",
              IKKE_IDENTIFISERT: "Personen er ikkje identifisert",
            },
          },
          vergeInfo:
            "Personen du har lagt til er under 18 år. Har du opplysningar om verje eller føresett? Då kan du leggja dei til med eigen knapp.",
        },
        beskrivelse: {
          leggTil: "Legg til ei beskriving av personen",
          tittel: "Beskriving",
          label: "Beskriv gjerningspersonen så godt du kan",
          helptext:
            "Til dømes kjønn, hårfarge og -lengd, anteken alder, høgd, klede, språk, arr osb.",
        },
        verge: {
          leggTil: "Legg til opplysningar om føresett eller verje",
          tittel: "Opplysningar om føresett eller verje",
          personnavn: {
            etternavn: {
              label: "Etternamn",
            },
            fornavn: {
              label: "Fornamn",
            },
          },
          kontaktopplysninger: {
            telefon: {
              label: "Telefonnummer",
            },
          },
          vergeKontaktet: {
            tittel: "Har du kontakta verja?",
            vergeErKontaktetSvarType: {
              JA: "Ja",
              NEI: "Nei",
            },
          },
          vergeTilStede: {
            tittel: "Kom verja til staden?",
            vergeTilStedeSvarType: {
              JA: "Ja",
              NEI: "Nei",
            },
          },
          panel: {
            personnavn: {
              fornavn: "Verjes fornamn",
              etternavn: "Verjes etternamn",
            },
            kontaktopplysninger: {
              telefon: "Verjes telefonnummer",
            },
          },
        },
        oppgitteOpplysninger: {
          leggTil: "Legg til oppgitt namn og/eller organisasjon",
          tittel: "Oppgitt namn og/eller organisasjon",
          helptext:
            "Du må fylla ut <strong>minst eitt</strong> felt, men gjerne så mange du kan",
          personnavn: {
            fornavn: "Kva fornamn oppgav personen?",
            etternavn: "Kva etternamn oppgav personen?",
          },
          virksomhetNavn:
            "Kva organisasjon eller verksemd sa personen at den representerte?",
          andreOpplysninger: "Andre opplysningar om oppgitt identitet",
          panel: {
            personnavn: {
              fornavn: "Oppgitt fornamn",
              etternavn: "Oppgitt etternamn",
            },
            virksomhetNavn: "Organisasjon",
            andreOpplysninger: "Andre opplysningar",
          },
        },
        kontaktopplysninger: {
          leggTil: "Legg til kontaktopplysningar",
          tittel: "Kontaktopplysningar",
          slett: "Slett",
          label: "Kva kontaktopplysningar har du?",
          helptext: "Kryss av for eitt eller fleire alternativ som passar",
          options: {
            TELEFONNUMMER: {
              label: "Telefonnummer",
              leggTil: "Legg til telefonnummer",
            },
            EPOST: {
              label: "E-postadresse",
              leggTil: "Legg til e-postadresse",
            },
            BRUKERNAVN: {
              label: "Brukarnamn",
              leggTil: "Legg til brukarnamn",
              context: "Kor vart det brukt?",
              contextHelptext: "Til dømes Finn.no, Whatsapp",
            },
            ADRESSE: {
              label: "Adresse",
              leggTil: "Legg til adresse",
            },
            ANNET: {
              label: "Anna",
              leggTil: "Legg til anna",
            },
          },
          panel: {
            brukernavn: {
              context: "Kor vart brukarnamnet brukt?",
            },
          },
        },
      },
    },
    gjerningsvirksomhet: {
      panel: {
        tittel: "Verksemd",
        navnMangler: "(namn manglar)",
      },
      sokefelt: {
        label: "Søk i verksemdsregisteret",
        helpText: "Søk på namn eller organisasjonsnummer",
        feilmeldingBrreg:
          "Fekk ikkje kontakt med Einingsregisteret, prøv igjen seinare",
      },
      leggTilManuelt: "Legg til verksemd manuelt",
      valgtVirksomhet: {
        opplysninger: "Opplysningar frå verksemdsregisteret",
        manuelleOpplysninger: "Opplysningar lagde til manuelt",
        endre: "Endre",
      },
      manuellVirksomhet: {
        modalTittel: "Legg til verksemd manuelt",
        virksomhetensNavn: {
          label: "Namnet på verksemda",
        },
        informasjon: {
          label: "Kva opplysningar har du om verksemda? (valfritt)",
          helpText: "Kryss av for eitt eller fleire alternativ som passar",
        },
        informasjonstyper: {
          NETTSTED: "Nettstad",
          E_POST: "E-post",
          TELEFON: "Telefonnummer",
          ADRESSE: "Adresse",
          ORGNR: "Organisasjonsnummer",
          ANNET: "Anna",
        },
        input: {
          NETTSTED: "Nettstad",
          E_POST: "E-postadresse",
          TELEFON: "Telefonnummer",
          ADRESSE: "Adresse",
          ORGNR: "Organisasjonsnummer",
          ANNET: "Andre opplysningar",
        },
        fullfoer: "Fullfør",
        avbryt: "Avbryt",
      },
      hvorforSkyldig: {
        label: "Kvifor meiner du at denne verksemda har gjort det?",
      },
      slett: "Slett verksemd",
    },
    radioKnapper: {
      fritekst: "Ja, eg kan gi ei skildring",
      kjent: "Ja",
      tittel: "Har du opplysningar om den eller dei du meiner har gjort det?",
      helpText:
        "Fleire mistenkte gjerningspersonar? Legg til éin gjerningsperson for kvar mistenkt",
      ukjent: "Nei, eg veit ikkje kven det er",
    },
    steg: {
      alder: {
        gjerningspersonAlderSvarType: {
          OVER_ATTEN: "Over 18 år",
          UNDER_ATTEN: "Under 18 år",
          UNDER_FEMTEN: "Under 15 år",
          VET_IKKE: "Veit ikkje",
        },
        label: "Kor gammal er gjerningspersonen?",
        title: "Alder",
      },
      begrunnelse: {
        begrunnelseForInvolvering: {
          label: "Kvifor meiner du at denne personen har gjort det?",
        },
        identifiseringsinformasjon: {
          beskrivelse: {
            ANNET: {
              label: "Beskriv korleis du har identifisert personen",
            },
            FREMVIST_ID: {
              label: "Kva slags ID er vist fram?",
            },
          },
          label: "Korleis er personen identifisert?",
          options: {
            ANNET: "Anna",
            FREMVIST_ID: "Framvist ID",
            IKKE_IDENTIFISERT: "Personen er ikkje identifisert",
          },
        },
        title: "Grunngiving",
      },
      personalia: {
        beskrivelse: "Andre opplysningar om gjerningspersonen",
        beskrivelseHelpText:
          "For eksempel kjønn, alder, hårfarge, alder, høgde, fasong, klede, språk, dialekt, arr, osv.",
        beskrivelseHelpTextBedrageri:
          "For eksempel flere brukarnam, språk/dialekt, oppgitt tilhørighet/organisasjon",
        title: "Personalia",
      },
      verge: {
        harKontaktinformasjonTilVerge: {
          title: "Har du kontaktinformasjon til verja?",
        },
        harVergesKontaktinformasjonSvarType: {
          JA: "Ja",
          NEI: "Nei",
        },
        kontaktopplysninger: {
          telefon: {
            label: "Telefonnummer",
          },
        },
        label: "Kontaktopplysningar til verje",
        personnavn: {
          etternavn: {
            label: "Etternamn",
          },
          fornavn: {
            label: "Fornamn",
          },
        },
        title: "Verje",
        vergeKontaktet: {
          tittel: "Har du kontakta verja?",
          vergeErKontaktetSvarType: {
            JA: "Ja",
            NEI: "Nei",
          },
        },
        vergeTilStede: {
          tittel: "Kom verja til staden?",
          vergeTilStedeSvarType: {
            JA: "Ja",
            NEI: "Nei",
          },
        },
      },
    },
  },
  hvemSaaDet: {
    anmelderErVitne: "Du blir registrert som eit vitne i saka",
    infoboks:
      "Vitne er viktige for å belyse kva som har skjedd. I tilfelle vi må hente inn meir informasjon, så treng vi kontaktinformasjon til eventuelle vitne.",
    input: {
      beskrivelse: "Andre opplysningar om vitnet",
      beskrivelseHelpText:
        "For eksempel rolle, tilsettforhold, kva vitnet har observert",
    },
    ja: "Ja",
    leggTilFlereVitner: "Legg til fleire vitne",
    leggTilVitne: "Legg til vitne",
    modal: {
      fullfoer: "Fullfør",
      lukk: "Lukk",
      tittel: "Om vitnet",
    },
    nei: "Nei",
    panel: {
      redigerAriaLabel: "Endre vitne {{nr}}",
      slett: "Slett",
      title: "Vitne",
    },
    saaAndre:
      "Veit du om andre vitne som såg at det skjedde eller har opplysningar om hendinga?",
    saaDu: "Såg du at hendinga skjedde?",
    vitne: "Vitne",
    vitneInformasjonOptions: {
      JA: "Ja",
      NEI: "Nei",
    },
  },
  hvordanSkjeddeDet: {
    tittel: "Korleis skjedde det?",
    infoboksTitle: "Hjelp oss å forstå korleis det skjedde.",
    TYVERI_SKADEVERK_INNBRUDD: {
      infoboks: {
        beskriv: "Beskriv så godt du kan kva som hende",
        bulletpoints: [
          "Kva som hende før, under og etter hendinga",
          "Informasjon om framgangsmåte",
          "Var du i kontakt med den eller dei du meiner er gjerningsperson?",
          "Kvifor meiner du at gjerningspersonane hadde som hensikt å stele?",
          "Korleis stilte gjerningspersonen seg til skuldinga?",
          "Var gjerningspersonen valdeleg eller trugande?",
        ],
        hjelpetekst:
          "Kva som er relevant informasjon varierer frå hending til hending, og alle detaljar kan vere viktige.",
      },
      inputLabel: "Forklar korleis det skjedde",
    },
    BEDRAGERI: {
      infoboks: {
        infotekst:
          "Din beskriving bør vere så utfyllande som mogleg. Detaljar er viktige. Skriv gjerne teksten kronologisk og forklar relevant historikk.",
        bulletInfo: "Typiske ting som er nyttig for oss å vita er:",
        bulletpoints: [
          "Kva hende før, under og etter hendinga",
          "Har du i forkant av denne hendinga fått nokon førespurnadar der nokon bad om personleg informasjon om deg?",
          "Dato for spesifikke hendingar viss det har pågått over tid",
          "Kva ville gjerningspersonen eller gjerningspersonane at du skulle gjera?",
          "Korleis kommuniserte du med gjerningspersonen eller gjerningspersonane?",
        ],
        bunnInfo:
          "Du har rett til å la vere å oppgi opplysningar som kan bidra til at eit nært familiemedlem eller ein annen person som står nær deg, blir straffa. Du har òg rett til å la vere å oppgi opplysningar som bryt teieplikta di.",
      },
      inputLabel: "Forklar korleis det skjedde",
    },
  },
  hvorOgNaar: {
    gjerningssted: {
      radioOptions: {
        ANNEN_ADRESSE: "Søk på adresse eller stad",
        IKKE_FYSISK_STED: "Hendinga kan ikkje knytast til ein fysisk stad",
        UTENLANDSK_ADRESSE: "I utlandet",
        KARTPUNKT: "Vel punkt i kart",
      } satisfies Record<
        Exclude<
          HendelsesstedValg,
          | "FORNAERMET_VIRKSOMHETSADRESSE"
          | "FORNAERMET_FREG_ADRESSE"
          | "ANNET_STED"
        >,
        string
      >,
      annenAdresseEllerSted: {
        annenRadio: "Søk på anna adresse eller stad",
        label: "Søk opp og vel ei adresse eller ein stad",
        slett: "Slett",
        oppslag: {
          forLang: "Ugyldig søk, maks 100 teikn",
          ulovligeTegn:
            'Unngå følgjande spesialteikn i søket : ; ! # % \\ ( ) { } [ ] "',
        },
        kartverketFeil: "Fekk ikkje kontakt med Kartverket, prøv igjen seinare",
        adresse: {
          bruksenhetsnummer: {
            label: "Vel brukseiningsnummer (valfritt)",
            panelLabel: "Vel brukseiningsnummer",
          },
        },
        sted: {
          beskrivelse: {
            label: "Beskriv kvar det skjedde",
            helpText:
              "Til dømes stasjonen du gjekk av på eller ei spesifikk sykkelparkering",
          },
        },
      },
      kart: {
        beskrivelse: {
          label: "Beskriv staden",
          panelLabel: "Beskriving",
        },
        breddegrad: "Breiddegrad",
        lengdegrad: "Lengdegrad",
      },
      ikkeFysiskSted: {
        label: "Beskriv kor det hende",
        helpText: "Til dømes sms, nettbutikk-adresse, namngitt dating-app",
        seOverLabel: "Beskriv kor det hende",
      },
      hvor: "Kor skjedde det?",
      virksomhet: {
        helpText: "Adresse henta frå Einingsregisteret",
        ingenAdresse: "Inga adresse på verksemd",
      },
      freg: {
        helpText: "Adresse hentet frå folkeregisteret",
      },
      utlandet: {
        land: "Søk og vel land",
        postnummer: "Postnummer",
        poststed: "Stad/By",
        adresse: "Gatenavn og husnummer",
      },
    },
    gjerningstidspunkt: {
      fra: {
        label: "Frå dato og klokkeslett",
        helpText: "dd.mm.åååå tt:mm",
      },
      helpText: "Om du er usikker kan du oppgi omtrentleg tidspunkt",
      legend: "I kva for tidsrom skjedde det?",
      naar: "Når skjedde det?",
      til: {
        label: "Til dato og klokkeslett",
        helpText: "dd.mm.åååå tt:mm",
      },
    },
  },
  kvittering: {
    sideHeader: "Kvittering på innsendt melding",
    forsikring: {
      info1: "Du kan bruke saksnummeret",
      info2: "i kontakt med forsikringsselskapet ditt",
      title: "Meldt på grunn av forsikring?",
    },
    header: {
      info: "Trykk her for å sjå meldinga di",
      title: "Saksnummer {{saksnummer}}",
    },
    bedrageriInfo: {
      info: "Svindlaren eller svindlarane kan prøva å lura deg igjen ved å gi seg ut for å vera nokon som skal hjelpa deg med saka (politi, bank eller liknande). Ikkje trykk på lenkjer eller oppgi kode eller passord på sider som du ikkje sjølv har oppsøkt via offentleg nettside eller nettbank.",
      title: "Vær forsiktig",
    },
    hvaSkjerVidere: {
      info: "Ei oppsummering av meldinga og ei kvittering på at vi har mottatt den blir sendt til din digitale postkasse eller per post. En saksbehandlar vil sjå på meldinga di og vurdere om politiet meiner det er hensiktsmessig å etterforske saka.",
      title: "Kva skjer vidare no?",
      link: "Les meir om kva som skjer når ei sak er meld her",
    },
    nyeOpplysninger: {
      title: "Nye opplysningar?",
      info: "Om du ønskjer å trekkja tilbake meldinga eller gi oss fleire opplysningar om saka, må du kontakta politidistriktet der hendinga skjedde og oppgi saksnummeret {{saksnummer}}.",
    },
    knapper: {
      gaaTilOversikt: "Gå til oversikta",
      til_oversikten: "Til oversikta",
      opprett: "Opprett ny melding",
    },
    saknr: "Straffesaksnummer",
    sendtInn: "Sendt inn",
    takk: {
      info: "Meldinga di bidrar til å gi oss oversikt over kriminaliteten i samfunnet vårt. Informasjonen frå meldinga er med på å auke kunnskapen vår og blir brukt til å førebygge at liknande hendingar skjer igjen.",
      title: "Takk for at du valde å melde",
    },
    tittel: "Kvittering",
  },
  navigerTilInnhold: "Hopp til hovedinnhald",
  omAnmelderOgFornaermet: {
    fornaermet: {
      konfliktraad: {
        info: {
          del1: "Det kan vere aktuelt å handtere saka i konfliktrådet. I konfliktrådet møtest gjerningspersonen og den fornærma etter eit lovbrot, og er ofte eit effektivt verkemiddel for å førebyggje kriminalitet.",
          del2: "Hvis vilkåra for behandling i konflikrådet er oppfylt må du kanskje signere ei egen samtykkeerklæring seinare.",
          lenketekst: "Du kan lese meir om konfliktsrådsbehandling her",
        },
        radioOptions: {
          JA: "Ja",
          NEI: "Nei",
          MER_INFO: "Eg ønsker meir info om konfliktrådet",
        },
        legacyRadioOptions: {
          VET_IKKE: "Veit ikkje",
        },
        title: "Konfliktråd",
        fornaermetVilligTilKonfliktraad: {
          label:
            "Er du eller den fornærma som du melder på vegne av villig til å behandle saka i konfliktrådet?",
        },
      },
      anmelder: {
        seg_selv: "Melder på vegner av meg sjølv",
      },
      fornaermetType: {
        fornaermetTypeOptions: {
          ANMELDER: "Meg sjølv",
          PERSON: "Ein annan person",
          VIRKSOMHET: "Ein bedrift eller organisasjon",
        },
        label: "Kven meldar du på vegner av?",
      },
      person: {
        freg: {
          info: "For å kunna leggja til ein fornærma person må du oppgi fullt namn og fødselsnummer, og desse opplysningane må stemma overeins med korleis personen er registrert i folkeregisteret",
          navn: {
            label: "Namn",
            helpText:
              "Det er viktig at du skriv fullt namn, slik det er registrert i folkeregisteret",
            valideringsfeil: "Du må oppgi eit namn",
          },
          fnr: {
            label: "Fødselsnummer / D-nummer",
            helpText: "Fyll ut 11 siffer, utan mellomrom eller bindestrek",
            valideringsfeil: "Fødselsnummeret må ha nøyaktig 11 siffer",
          },
          submit: {
            label: "Søk i folkeregisteret",
          },
          endre: {
            label: "Forkast og søk igjen",
          },
          error: {
            notFound: {
              title: "Finner ikkje treff på namn og fødselsnummer",
              message_zero:
                "Du har søkt feil for mange gonger, og har ingen fleire forsøk igjen. Prøv igjen seinare.",
              message_one:
                "Me finn ingen personar i folkeregisteret med denne kombinasjonen av namn og fødselsnummer. Pass på at opplysningane stemmer og prøv igjen. Du har {{count}} forsøk igjen",
              message_other:
                "Me finn ingen personar i folkeregisteret med denne kombinasjonen av namn og fødselsnummer. Pass på at opplysningane stemmer og prøv igjen. Du har {{count}} forsøk igjen",
            },
            forbidden: {
              title: "Ingen fleire søk",
              message: "Du har søkt feil for mange gonger. Prøv igjen seinare.",
            },
            unknown: {
              title: "Ein ukjend feil oppstod",
              message: "Ein ukjend feil oppstod. Prøv igjen seinare.",
            },
          },
        },
        beskrivelse: {
          label: "Andre opplysningar om fornærma (valfritt)",
          se_over: "Andre opplysningar om fornærma",
        },
        epost: {
          label: "E-postadresse (valfritt)",
          se_over: "E-postadresse",
        },
        etternavn: {
          label: "Etternamn",
        },
        fornavn: {
          label: "Fornamn",
        },
        relasjonstyper: {
          ANNET: "Anna",
          FAMILIE: "Familie",
          VERGE: "Verje",
        },
        telefonnummer: {
          label: "Telefonnummer",
        },
        tilknytning: {
          label: "Kva slags relasjon har du til den fornærma?",
        },
        tilknytningBeskrivelse: {
          label: "Beskriv relasjonen din til den fornærma",
          se_over: "Skildring av relasjonen",
        },
      },
      tittel: "Om den fornærma",
      virksomhet: {
        valgtVirksomhet: {
          slett: "Slett",
          beliggenhetsadresse: "Plasseringsadresse",
          aktivitet: "Aktivitet",
        },
        orgnummer: "Organisasjonsnummer",
        relasjonstyper: {
          ANNET: "Anna",
          ANSATT: "Tilsett",
          LEDER: "Dagleg leiar eller anna leiarrolle",
          VEKTER: "Vektar",
        },
        samtykkeTyper: {
          JA: "Ja",
          NEI: "Nei",
        } satisfies Record<SamtykkeVirksomhetKanSeAnmeldelsen, string>,
        samtykkeVirksomhetKanSeAnmeldelsen: {
          label: "Ønsker du å sende ein kopi av din melding til verksemda?",
          helptext:
            "Kopien inneheld kontaktinformasjonen din (ikkje fødselsnummer), og dessutan informasjon om stolne/skadde gjenstandar og kvar og når hendinga skjedde",
        },
        sokefelt: {
          feilmeldingBrreg:
            "Fekk ikkje kontakt med Einingsregisteret, prøv igjen seinare",
          helpText:
            "Søk opp namn eller organisasjonsnummer. Vel undereininga der lovbrotet skjedde",
          hovedenhet: "Hovedeining",
          label: "Kva for verksemd melder du for?",
          underenhet: "Undereining",
        },
        tilknytning: {
          label: "Kva slags tilknyting har du til verksemda?",
          se_over: "Tilknyting til verksemda",
        },
        tilknytningBeskrivelse: {
          label: "Beskriv tilknytinga du har til verksemda",
        },
        tittel: "Om verksemda",
        vekterSelskap: {
          feilmeldingBrreg:
            "Fekk ikkje kontakt med Einingsregisteret, prøv igjen seinare.",
          helpText: "Søk opp namn eller organisasjonsnummer",
          label: "Kva for vektarselskap jobbar du for?",
          se_over: "Vektarselskap",
        },
        virksomhetensReferanse: {
          label: "Saksreferansen for verksemda",
          helpText:
            "Referansenummer til den interne saksbehandlinga for verksemda dersom det er aktuelt",
        },
      },
    },
    om_deg: {
      epost: "E-postadresse",
      etternavn: "Etternamn",
      fnr: "Fødselsnummer / D-nummer",
      fornavn: "Fornamn",
      fregFeil: {
        henteknapp: "Prøv igjen",
        henter: "Hentar",
        hjelpetekst: "Prøv igjen eller kom tilbake seinare",
        tittel: "Kunne ikkje hente namnet ditt frå folkeregisteret",
      },
      hjelpetekst: "Namnet ditt er henta frå Folkeregisteret",
      mellomnavn: "Mellomnamn",
      navn: "Namn",
      skatteetaten:
        "Dersom noko er feil, må du endre opplysningane i Folkeregisteret via skatteetaten.no",
      telefon: "Telefonnummer",
      tittel: "Om deg",
    },
    slett_anmeldelse: {
      modal: {
        avbryt: "Avbryt",
        hjelpetekst:
          "Er du sikker på at du vil slette den påbegynna meldinga for godt? Du kan ikkje angre.",
        label: "Slette utkast?",
        slett: "Ja, slett",
      },
      slett: "Slett",
    },
  },
  oppsummering: {
    anmeldelse: "Innsend melding",
    notifikasjon: {
      info: "Du kan be om innsyn i straffesaksdokumentet ved å ta kontakt med politidistriktet der saka blir behandla. Saka blir som regel behandla av politidistriktet der den melde hendinga skjedde.",
      tittel:
        "Du kan ikkje sjå resten av innhaldet i meldinga fordi det utgjer eit tausheitsbelagt straffesaksdokument",
    },
    skrivUt: "Skriv ut sida",
    saknr: "Straffesaksnummer",
    sendtInn: "Sendt inn",
    tittel: "Innsend melding",
    diga: {
      mangler_saksnr: "Saksnummer manglar, ta kontakt med politiet ved behov",
      lovbrudd: {
        label: "Lovbrot",
        type: {
          TYVERI_AV_SYKKEL: "Tjuveri av sykkel",
          TYVERI_AV_MOBILTELEFON: "Tjuveri av mobiltelefon",
          TYVERI_FLERE_GJENSTANDER: "Tjuveri av andre gjenstandar",
          KJENNEMERKE: "Tjuveri av kjennemerke (registreringsskilt)",
          SKADEVERK: "Skadeverk på privat eigedom, gjenstandar eller køyretøy",
          KRENKELSE: "Ulovleg bruk av fast eigedom",
          BRUKSTYVERI: "Brukstjuveri av køyretøy eller båt frå privatperson",
        } satisfies Record<DigaAnmeldelseType, string>,
      },
      gjerningssted: {
        type: {
          options: {
            ANNET_OFFENTLIG_STED: "Annan offentleg stad i Noreg",
            BUTIKK: "Forretning/butikk i Noreg",
            IDRETTS_ELLER_KULTURSTED: "Idretts- og kulturstad i Noreg",
            KOLLEKTIVTRANSPORT: "Kollektivtransport i Noreg",
            OMSORGSINSTITUSJON: "Omsorgsinstitusjon i Noreg",
            SERVERINGSSTED: "Restaurant, severingssted i Noreg",
            UTLAND: "Hendinga skjedde i utlandet",
            VED_BOPEL: "Ved eller utanfor bustad i Noreg",
          } satisfies Record<GjerningsstedType, string>,
          label: "Type gjerningsstad",
        },
        beskrivelse: "Beskriving av gjerningsstad",
      },
      beskrivelse: "Beskriving av hending",
      gjenstandBeskrivelse: "Beskriving av gjenstanda(ne)",
      skadeverk: {
        type: {
          label: "Type skadeverk",
          options: {
            KJORETOY: "Skadeverk på bil, motorsykkel, moped",
            BYGNING: "Skadeverk på eller tilgrisning av bygning",
            KNUST_RUTE: "Skadeverk ved ruteknusing",
            TAGGING: "Skadeverk ved tagging/graffiti",
            BAAT: "Skadeverk på båt",
            ANNET: "Anna skadeverk",
          } satisfies Record<SkadeverkType, string>,
        },
        beskrivelse: "Beskriving av skaden",
      },
      brukstyveri: {
        type: {
          label: "Type brukstjuveri",
          options: {
            KJORETOY: "Brukstjuveri av køyretøy",
            FARTOY: "Brukstjuveri av fartøy",
            ANNET: "Brukstjuveri av anna motordrive køyretøy eller fartøy",
          } satisfies Record<BrukstyveriType, string>,
        },
        kjoretoyBeskrivelse: "Beskriving av køyretøy",
      },
    },
  },
  personSkjema: {
    legend: "Personalia",
    epost: "E-postadresse",
    etternavn: "Etternamn",
    fjern: "Fjern",
    foedselsdato: {
      helpText: "dd.mm.åååå",
      label: "Fødselsdato",
    },
    foedselsEllerDnummer: {
      helpText: "Fødselsnummer / D-nummer",
      label: "Personleg ID (11 siffer)",
    },
    fornavn: "Fornamn",
    helptext:
      "Du må fylle ut <strong>minst eitt</strong> av felta, men gjerne så mange du kan",
    telefon: "Telefonnummer",
    ekstraKontaktopplysninger: {
      brukernavn: "Brukarnamn",
      brukernavnContext: "Kor vart brukarnamnet brukt?",
      brukernavnContextHelptext: "For eksempel Finn.no, Whatsapp",
    },
  },
  se_over: {
    endre_steg: "Endre steg",
    bekreftelseFalskAnmeldelse: {
      checkbox:
        "Eg er kjent med at det er straffbart å levere ei falsk melding til politiet etter §§222 — 225 i straffelova",
      notifikasjon: "Du må bekrefte at du har forstått ditt ansvar som melder",
    },
    gjerningsperson: {
      gjerningspersoner: "Informasjon om antatt gjerningsperson",
      label: "Har du opplysningar om den eller dei du meiner har gjort det?",
    },
    hva_har_skjedd: "Kva skjedde?",
    hvordan_skjedde_det: "Korleis skjedde det?",
    ingen_svar: "Ingen svar",
    oppsummering_av_feil: {
      title:
        "Følgjande felt må vera rett utfylt før du kan senda inn meldinga:",
    },
    modal: {
      feilet: "Noko gjekk gale ved innsending",
      feiletInfo:
        "Noko gjekk gale, og meldinga di blei ikkje sendt inn. Prøv igjen, eller kom tilbake seinare.",
      lukk: "Lukk",
      provIgjen: "Prøv igjen",
      senderInn: "Sender inn meldinga…",
      suksess: "Meldinga er sendt inn",
    },
    om_deg: "Om deg",
    om_anmelder: "Om deg",
    om_fornaermede: "Om den fornærma",
    om_virksomheten: "Om verksemda",
    send: "Send inn melding",
    tittel: {
      TYVERI_SKADEVERK_INNBRUDD: "Tjuveri, innbrot eller skadeverk",
      BEDRAGERI: "Svindel og ID-tjuveri",
    },
    vedlegg: {
      beskrivelse: "Skildring",
      filnavn: "Filnamn",
    },
    vitnerlabel: "Kontaktinformasjon til vitnet",
  },
  skjema_context: {
    lagrer: "Lagrar",
    sender_inn: "Sender inn",
    sist_lagret: "Sist lagra",
    til_oversikten: "Til oversikta",
    tittel: "Melding",
  },
  skjemaknapper: {
    gaa_tilbake: "Gå til førre steg",
    gaa_videre: "Gå vidare",
  },
  standard: {
    valgfritt: "(valfritt)",
  },
  steg: {
    erstatningskrav: "Erstatningskrav",
    hva_har_skjedd: "Kva har skjedd?",
    hvem_gjorde_det: "Kven gjorde det?",
    hvem_saa_det: "Kven har opplysningar?",
    hvor_og_naar: "Kor og når skjedde det?",
    konfliktraad: "Behandling i konfliktråd",
    om_deg: "Om deg",
    om_deg_og_fornaermede: "Om deg og den fornærma",
    se_over: "Sjå over og send inn",
    vedlegg: "Vedlegg",
  },
  vedlegg: {
    beskrivelse: "Gi ein kort skildring av fila (valfritt)",
    wasmSupport:
      "Nettlesaren manglar nødvendig funksjonalitet for å lasta opp vedlegg. Oppdater nettlesaren eller ta i bruk ein nyare nettlesar, f.eks Google Chrome",
    beskrivelseHjelpetekst:
      "Er fila ein video? Oppgi gjerne tidspunkt som er relevante å sjå på.",
    hjelpetekst:
      "Du kan laste opp inntil  {{ maksAntallVedlegg }} filer. Formatar som støttas er {{ lovligeFiltyper }}.",
    infotekst: {
      BEDRAGERI: {
        bulletpoints: [
          "Skjermbilete av dialog med svindlaren eller svindlarane",
          "Kontoutskrift som viser relevante overføringar",
          "Skjermbilete av aktuelle nettbutikkar, annonsar eller QR-koder",
          "Lydopptak av telefonsamtale",
          "Samtalelogg som viser aktuell telefonsamtale",
        ],
        hjelpetekst:
          "Viss du har bilete, video eller andre filer som kan bidra til å klargjera kva som har skjedd, kan du lasta det opp her. Eksempel på vedlegg er:",
      },
      TYVERI_SKADEVERK_INNBRUDD: {
        bulletpoints: [
          "Overvakingsvideo",
          "Stillbilete eller videoklipp som kan identifisere gjerningspersonen",
          "Bilete av skadar",
          "Dokumentasjon på stolne eller øydelagde gjenstandar, til dømes kvittering",
        ],
        hjelpetekst:
          "Dersom du har bilete, video eller andre filer som kan bidra til å belyse kva som har skjedd, kan du laste dei opp her. Eksempel på vedlegg er:",
      },
      label: "Last opp filer (valfritt)",
    },
    lastOpp: "Last opp filer",
    opplastingFeilet: "Kunne ikkje lagre vedlegget",
    slett: "Slett vedlegg",
    status: {
      KLARGJOER: "Klargjer vedlegget for opplasting",
      OPPRETTET: "Klargjer vedlegget for opplasting",
      LASTER_OPP: "Lastar opp",
      SCANNER: "Scanner vedlegg for virus",
      FEILET: "Feilet",
      FEILET_FOR_OPPRETTELSE: "Feilet",
      OPPLASTET: "Opplastet",
    },
    tittel: "Last opp vedlegg",
    lukkeUtenOpplasting:
      "Viss du forlèt denne sida, vert opplastinga av vedlegg avbroten. Er du sikker på at du vil forlate denne sida?",
  },
  samtykkeInformasjon: {
    infotekst: {
      bulletpoints: [
        "For å kunna etterforska svindel og ID-tjuveri, kan det bli aktuelt å innhenta fleire opplysningar frå private aktørar som bankar, kredittselskap eller låneinstitusjonar. Det kan til dømes vera opplysningar om kundeforholdet eller loggane ditt knytt til datoar eller kontoar oppgitt i denne meldinga.",
        "Vi ber derfor om ditt samtykke til å henta inn desse opplysningane.",
        "Du treng ikkje gi samtykke til innhenting av opplysningar for å kunna levera ei melding.",
        "Du kan når som helst kontakta politiet eller ovannemnde selskap og trekkja samtykket, politiet kan likevel velja å behalda dei innhenta opplysningane dersom dei er nødvendige for etterforskinga.",
      ],
    },
    samtykkeInnhentingAvOpplysninger: {
      ja: "Ja",
      nei: "Nei",
      legend:
        "Samtykkjer du til at politiet kan innhenta opplysningar og dokumentasjon om dine kunde-/kontoforhold?",
    },
  },
};

export default skjematekst;
